import { Payer } from "@redotech/congo-enums/payer";
import { ReturnPolicyStoreCreditType } from "@redotech/crdb-enums/returns/return-policy-store-credit-type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  ExchangeFinancialStrategy,
  ExchangeOrderLineItemPriceSource,
} from "../exchange-settings";
import { Step } from "../return-flow";
import { InputContentType } from "../return-flow-enums";
import {
  AdjustmentZodSchema,
  AllowedExchangeTypesZodSchema,
  RepairFeeConfigZodSchema,
} from "../return-flow-schema";
import {
  ConditionExprSchema,
  everyMatchSchema,
  firstMatchSchema,
  perMethodFirstMatchSchema,
} from "./rules";

export const returnPolicyMethods = [
  "exchange",
  "store_credit",
  "refund",
  "repair",
] as const;
const MethodSchema = z.enum(returnPolicyMethods);

const CurrencyOverridesSchema = z.record(z.string(), z.number());
const PayerSchema = z.union([
  z.literal(Payer.Customer),
  z.literal(Payer.Merchant),
]);

export const ReturnWindowSchema = firstMatchSchema(
  z.strictObject({
    days: z.number().int().nullable(),
    anchor: z.enum(["order", "shipped", "fulfilled", "delivered"]),
    message: z.string(),
  }),
);

export const AcceptedCountriesSchema = z.strictObject({
  countries: z.array(z.string()).nullable(),
  message: z.string(),
});

export const ExcludedProductsSchema = z.strictObject({
  tags: z.array(z.string()),
  collections: z.array(z.string()),
  productIds: z.array(z.string()),
  message: z.string(),
});

export const RejectReturnRulesSchema = everyMatchSchema(
  z.strictObject({ message: z.string() }),
);

/** Stops the item's return with a message, without recording a rejection. A block step also keeps its heading and phase. */
export const BlockReturnRulesSchema = everyMatchSchema(
  z.strictObject({
    message: z.string(),
    heading: z.string().optional(),
    phase: z.enum(["before_reasons", "after_reasons"]).optional(),
  }),
);

export const ReturnMethodsOfferedSchema = firstMatchSchema(
  z.array(MethodSchema),
);

export const StoreCreditTypeSchema = createEnum(ReturnPolicyStoreCreditType, {
  enumName: "ReturnPolicyStoreCreditType",
});

export const ExchangeTypesOfferedSchema = firstMatchSchema(
  AllowedExchangeTypesZodSchema,
);

export const ExchangeAgentSettingsSchema = firstMatchSchema(
  z.strictObject({
    disableExchangeAgent: z.boolean(),
    disableVariantRecommendations: z.boolean(),
    disableProductRecommendations: z.boolean(),
  }),
);

export const ExchangeOrderShippingPayerSchema = firstMatchSchema(PayerSchema);

export const TagsToAddToExchangeOrderSchema = everyMatchSchema(
  z.strictObject({
    tags: z.array(z.string()),
    copyOriginalOrderTags: z.boolean(),
  }),
);

export const ExchangeOrderShippingLineNameSchema = firstMatchSchema(
  z.string().nullable(),
);

export const ExchangeOrderShippingRatesSchema = firstMatchSchema(
  z.array(
    z.strictObject({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
      price: z.number(),
    }),
  ),
);

export const HideTaggedProductsFromExchangesSchema = z.strictObject({
  tags: z.array(z.string()),
  collections: z.array(z.string()),
});

export const ExchangeAddOnOfferSchema = firstMatchSchema(
  z
    .strictObject({
      variantId: z.string(),
      title: z.string(),
      acceptButton: z.string(),
      rejectButton: z.string(),
    })
    .nullable(),
);

export const ItemAdjustmentsByMethodSchema =
  perMethodFirstMatchSchema(AdjustmentZodSchema);

/** Replace the item adjustment with the team's return adjustment function output. */
export const UseAdjustmentFunctionByMethodSchema = perMethodFirstMatchSchema(
  z.boolean(),
);

export const ReturnLevelAdjustmentsSchema =
  everyMatchSchema(AdjustmentZodSchema);

export const DynamicBundleAdjustmentSchema = z.strictObject({
  enabled: z.boolean(),
});

export const DiscountCodeNameFormatSchema = firstMatchSchema(
  z
    .strictObject({
      prefix: z.string().optional(),
      suffix: z.string().optional(),
    })
    .nullable(),
);

export const ReturnShippingFeeSchema = firstMatchSchema(
  z.strictObject({
    payer: PayerSchema.nullable(),
    flat: z
      .strictObject({
        amount: z.string(),
        currencyOverrides: CurrencyOverridesSchema.optional(),
        perCurrencyAmountsEnabled: z.boolean().optional(),
      })
      .nullable(),
  }),
);

export const RequireSendBackSchema = firstMatchSchema(z.boolean());

/** Decided once for the return after the walk; non-null replaces every item's `require_send_back`. */
export const RequireSendBackOverrideSchema = firstMatchSchema(
  z.boolean().nullable(),
);

export const ReturnLabelProviderSchema = firstMatchSchema(
  z.enum(["redo", "customer", "merchant", "shopper_choice"]),
);

export const ReturnLabelUpchargeSchema = firstMatchSchema(
  z
    .strictObject({
      type: createEnum(Step.LabelUpchargeType, {
        enumName: "LabelUpchargeType",
      }),
      value: z.string(),
    })
    .nullable(),
);

export const DropoffProviderFeesSchema = firstMatchSchema(
  z.strictObject({
    enabled: z.boolean(),
    fees: z.array(
      z.strictObject({
        provider: z.string(),
        fee: z.string(),
        currencyOverrides: CurrencyOverridesSchema.optional(),
        perCurrencyAmountsEnabled: z.boolean().optional(),
      }),
    ),
  }),
);

export const MerchantCoversPackagePickupSchema = firstMatchSchema(z.boolean());

export const PackagePickupSchema = z.strictObject({
  enabledForHeavyItems: z.boolean(),
  maxCoveredAmount: z.number().nullable(),
});

export const ReimburseForFinalSaleSchema = firstMatchSchema(z.boolean());

export const RepairFeeSchema = firstMatchSchema(
  z.strictObject({
    chargeFee: z.boolean(),
    config: RepairFeeConfigZodSchema.nullable(),
    labelPayer: PayerSchema.nullable(),
  }),
);

const QuestionOptionSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  additionalAiInfo: z.string().nullable(),
});

/** Asked in list order at the reasons step; `when` may read earlier reason questions. The chosen option names form the item's reason chain. */
export const ReturnReasonQuestionSchema = z.strictObject({
  id: z.string(),
  when: ConditionExprSchema.nullable(),
  message: z.string(),
  description: z.string().nullable(),
  additionalAiInfo: z.string().nullable(),
  options: z.array(QuestionOptionSchema),
  shuffle: z.boolean(),
});
export type ReturnReasonQuestion = z.infer<typeof ReturnReasonQuestionSchema>;

export const ReturnReasonsSchema = firstMatchSchema(
  z.strictObject({
    mode: z.enum(["list", "ai"]),
    questions: z.array(ReturnReasonQuestionSchema),
    ai: z
      .strictObject({
        maxQuestions: z.number().int().nullable(),
        customGuidance: z.array(
          z.strictObject({ id: z.string(), description: z.string() }),
        ),
        customQuestion: z.string().nullable(),
        customExplanationSubtext: z.string().nullable(),
        merchantGuidance: z.string().nullable(),
        additionalAiInfo: z.string().nullable(),
      })
      .nullable(),
  }),
);

const TextValidationSchema = z.strictObject({
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  contentType: createEnum(InputContentType, {
    enumName: "InputContentType",
  }).optional(),
  customRegex: z.string().optional(),
  customRegexErrorMessage: z.string().optional(),
  useAdvancedValidation: z.boolean().optional(),
});

const QuestionBaseSchema = z.strictObject({
  id: z.string(),
  phase: z.enum(["before_reasons", "after_reasons"]),
  when: ConditionExprSchema.nullable(),
  message: z.string(),
  description: z.string().nullable(),
  additionalAiInfo: z.string().nullable(),
});

export const QuestionSchema = z.discriminatedUnion("kind", [
  QuestionBaseSchema.extend({
    kind: z.literal("multiple_choice"),
    options: z.array(QuestionOptionSchema),
    shuffle: z.boolean(),
  }),
  QuestionBaseSchema.extend({
    kind: z.literal("input"),
    inputType: z.enum(["text", "image", "video"]),
    required: z.boolean(),
    exampleImage: z.string().nullable(),
    textValidation: TextValidationSchema.nullable(),
    aiVerification: z.string().nullable(),
    aiVerificationStrictness: z.number().nullable(),
  }),
  QuestionBaseSchema.extend({
    kind: z.literal("information"),
    header: z.string(),
    continueButtonText: z.string(),
  }),
]);
export type Question = z.infer<typeof QuestionSchema>;
export type ReturnReasons = z.infer<typeof ReturnReasonsSchema>;
export const QuestionsSchema = z.array(QuestionSchema);

export const MerchantReviewBeforeFilingSchema = firstMatchSchema(z.boolean());

export const AllowUnpublishedReplacementSchema = firstMatchSchema(z.boolean());

export const RequireManualReviewSchema = firstMatchSchema(
  z.strictObject({ enabled: z.boolean(), reason: z.string().nullable() }),
);

export const FlagForReviewSchema = everyMatchSchema(z.string().nullable());

export const TagsToAddToReturnSchema = everyMatchSchema(
  z.array(z.strictObject({ name: z.string(), color: z.string() })),
);

export const IgnoreInventoryForReturnTypesSchema = z.array(MethodSchema);

export const exchangeOrderCreateTriggers = [
  "created",
  "created_upsells",
  "approved",
  "exchange_processed",
  "all_processed",
] as const;

export const cheaperExchangeDifferenceOptions = [
  "store_credit",
  "refund",
  "customer_chooses",
] as const;

export const outOfStockExchangeOutcomes = ["store_credit", "refund"] as const;

export const instantExchangeCardAuthorizations = [
  "one_dollar",
  "full",
  "none",
] as const;

export const exchangeVariantPriceDifferences = ["charge", "ignore"] as const;

export const exchangeShoppingLocations = ["portal", "storefront"] as const;

export const labelExpirationAnchors = ["return_created", "delivery"] as const;

export const restockItemsOptions = ["all", "undamaged", "none"] as const;

export const outOfStockExchangeItemsOptions = [
  "cancel",
  "fulfill_available",
  "manual",
  "oversell",
] as const;

const PositiveIntSchema = z.number().int().positive();

const ExchangeRegionLocationMappingsSchema = firstMatchSchema(
  z.array(z.strictObject({ country: z.string(), locationId: z.string() })),
);

/** Tags added to the original order when this compensation is issued. */
const TagsToAddToOrderSchema = everyMatchSchema(
  z.strictObject({ tags: z.array(z.string()) }),
);

export const ReturnPolicySnapshotSettingsSchema = z.strictObject({
  return_window: ReturnWindowSchema,
  accepted_countries: AcceptedCountriesSchema,
  excluded_products: ExcludedProductsSchema,
  reject_return_rules: RejectReturnRulesSchema,
  block_return_rules: BlockReturnRulesSchema,
  return_methods_offered: ReturnMethodsOfferedSchema,
  // jsonb string of ReturnPolicyStoreCreditType. No rules yet.
  store_credit_type: StoreCreditTypeSchema,
  exchange_types_offered: ExchangeTypesOfferedSchema,
  exchange_agent_settings: ExchangeAgentSettingsSchema,
  exchange_order_shipping_payer: ExchangeOrderShippingPayerSchema,
  tags_to_add_to_exchange_order: TagsToAddToExchangeOrderSchema,
  exchange_order_shipping_line_name: ExchangeOrderShippingLineNameSchema,
  exchange_order_shipping_rates: ExchangeOrderShippingRatesSchema,
  hide_tagged_products_from_exchanges: HideTaggedProductsFromExchangesSchema,
  carry_over_line_item_properties: firstMatchSchema(z.boolean()),
  carry_over_line_item_properties_on_add_on_accept: firstMatchSchema(
    z.boolean(),
  ),
  exchange_add_on_offer: ExchangeAddOnOfferSchema,
  allow_unpublished_replacement: AllowUnpublishedReplacementSchema,
  item_adjustments_by_method: ItemAdjustmentsByMethodSchema,
  use_adjustment_function_by_method: UseAdjustmentFunctionByMethodSchema,
  return_level_adjustments: ReturnLevelAdjustmentsSchema,
  // jsonb columns, parsed as a fixed value. FirstMatch rules are rejected
  // until a setting is deliberately opened up.
  use_it_or_lose_it_bonus: z.boolean(),
  keep_bonus_on_advanced_exchange: z.boolean(),
  shop_now_bonus_eligible: z.boolean(),
  apply_adjustment_as_discount_code: firstMatchSchema(z.boolean()),
  adjustment_discount_title: z.string().nullable(),
  waive_refund_store_credit_below_amount: z.string().nullable(),
  dynamic_bundle_adjustment: DynamicBundleAdjustmentSchema.nullable(),
  discount_code_name_format: DiscountCodeNameFormatSchema,
  return_shipping_fee: ReturnShippingFeeSchema,
  require_send_back: RequireSendBackSchema,
  require_send_back_override: RequireSendBackOverrideSchema,
  return_label_provider: ReturnLabelProviderSchema,
  return_label_upcharge: ReturnLabelUpchargeSchema,
  dropoff_provider_fees: DropoffProviderFeesSchema,
  merchant_covers_package_pickup: MerchantCoversPackagePickupSchema,
  package_pickup: PackagePickupSchema,
  reimburse_for_final_sale: ReimburseForFinalSaleSchema,
  repair_fee: RepairFeeSchema,
  return_reasons: ReturnReasonsSchema,
  questions: QuestionsSchema,
  require_manual_review: RequireManualReviewSchema,
  merchant_review_before_filing: MerchantReviewBeforeFilingSchema,
  flag_for_review: FlagForReviewSchema,
  tags_to_add_to_return: TagsToAddToReturnSchema,
  ignore_inventory_for_return_types: IgnoreInventoryForReturnTypesSchema,
  store_credit_expires: firstMatchSchema(z.boolean()),
  store_credit_expiration_days: firstMatchSchema(PositiveIntSchema),
  refund_duties: firstMatchSchema(z.boolean()),
  exchange_order_create_trigger: firstMatchSchema(
    z.enum(exchangeOrderCreateTriggers),
  ),
  exchange_region_filtering: firstMatchSchema(z.boolean()),
  exchange_region_location_mappings: ExchangeRegionLocationMappingsSchema,
  exchange_region_default_location: firstMatchSchema(z.string().nullable()),
  exchange_order_pricing: firstMatchSchema(
    createEnum(ExchangeFinancialStrategy, {
      enumName: "ExchangeFinancialStrategy",
    }),
  ),
  exchange_order_item_price_source: firstMatchSchema(
    createEnum(ExchangeOrderLineItemPriceSource, {
      enumName: "ExchangeOrderLineItemPriceSource",
    }),
  ),
  exchange_order_include_taxes: firstMatchSchema(z.boolean()),
  cheaper_exchange_difference: firstMatchSchema(
    z.enum(cheaperExchangeDifferenceOptions),
  ),
  exchange_recommendation_limit: firstMatchSchema(PositiveIntSchema),
  out_of_stock_exchange_outcome: firstMatchSchema(
    z.enum(outOfStockExchangeOutcomes),
  ),
  instant_exchanges: firstMatchSchema(z.boolean()),
  instant_exchange_return_deadline_days: firstMatchSchema(PositiveIntSchema),
  instant_exchange_card_authorization: firstMatchSchema(
    z.enum(instantExchangeCardAuthorizations),
  ),
  instant_exchanges_only: firstMatchSchema(z.boolean()),
  exchange_change_shipping_address: firstMatchSchema(z.boolean()),
  exchange_variant_price_difference: firstMatchSchema(
    z.enum(exchangeVariantPriceDifferences),
  ),
  exchange_shopping_location: firstMatchSchema(
    z.enum(exchangeShoppingLocations),
  ),
  bonus_label: firstMatchSchema(z.string().nullable()),
  fee_label: firstMatchSchema(z.string().nullable()),
  offer_package_pickup: firstMatchSchema(z.boolean()),
  in_store_returns: firstMatchSchema(z.boolean()),
  label_expiration: firstMatchSchema(z.enum(labelExpirationAnchors)),
  reopen_expired_returns: firstMatchSchema(z.boolean()),
  reopen_window_days: firstMatchSchema(PositiveIntSchema),
  flag_reopened_returns: firstMatchSchema(z.boolean()),
  tags_to_add_to_order_on_store_credit: TagsToAddToOrderSchema,
  tags_to_add_to_order_on_refund: TagsToAddToOrderSchema,
  restock_items: firstMatchSchema(z.enum(restockItemsOptions)),
  claim_duty_drawback: firstMatchSchema(z.boolean()),
  duty_drawback_countries: firstMatchSchema(z.array(z.string())),
  out_of_stock_exchange_items: firstMatchSchema(
    z.enum(outOfStockExchangeItemsOptions),
  ),
});
export type ReturnPolicySnapshotSettings = z.infer<
  typeof ReturnPolicySnapshotSettingsSchema
>;
